import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";

import moment from "moment-timezone";

import Button from "@mui/material/Button";
import Skeleton from "@mui/material/Skeleton";

import { useAuth } from "../hooks/useAuth";
import useDish from "../hooks/useDish";
import usePageTitle from "../hooks/usePageTitle";
import Modal from "../Components/Modal";

function Dish() {
  const { id } = useParams();
  const { authed, cityId } = useAuth();
  const { dish, dishEvents, error, loading } = useDish(id);
  const [isNonAuthedModalOpen, toggleIsNonAuthedModalOpen] = useState(false);

  const inThreeHours = moment().add(3, "h");

  const activeEvents = dishEvents?.filter((event) => {
    return moment(event.date).isAfter(inThreeHours);
  });
  const otherLocations = dish?.locations.filter(loc => loc.city.id !== cityId);
  const navigate = useNavigate();

  useEffect(() => {
    if (error) {
      navigate("/dishes");
    }
  }, [error, navigate]);

  usePageTitle(`${dish?.title} | Fudopia | Meet. Eat. Repeat.`);

  const handleJoinEventClicked = (eventId) => {
    if (authed) {
      navigate(`/events/${eventId}`);
    } else {
      toggleIsNonAuthedModalOpen(true);
    }
  };
  function NonAuthedModal() {
    return (
      <Modal
        title="Be one of the first foodies in Bangkok!"
        isOpen={isNonAuthedModalOpen}
        handleDismiss={() => toggleIsNonAuthedModalOpen(!isNonAuthedModalOpen)}
      >
        <p style={{ textAlign: "center" }}>
          We’re offering free event access for the first 200 signups. No
          subscription needed for a whole year.
        </p>
        <p style={{ marginBottom: 40, textAlign: "center" }}>
          Create or join foodie meetups and help us kick things off in the
          tastiest way.
        </p>
        <div className="btn-inline">
          <Button
            color="primary"
            variant="contained"
            onClick={() => toggleIsNonAuthedModalOpen(!isNonAuthedModalOpen)}
          >
            Not now
          </Button>
          <Button
            color="secondary"
            variant="contained"
            onClick={() => navigate("/login")}
          >
            Login/Register
          </Button>
        </div>
      </Modal>
    );
  }
  return (
    <>
      <NonAuthedModal />
      {error && <p className="error-message">Something went wrong...</p>}
      {loading && (
        <div className="block-wrapper">
          <Skeleton animation="wave" height={600}></Skeleton>
        </div>
      )}
      {!loading && dish && (
        <div>
          <div className="block-wrapper">
            <div className="block dish">
              <div className="image-wrapper">
                <img
                  src={dish.image_url}
                  className="dish-image"
                  alt={dish.title}
                />
              </div>
              <div className="content-wrapper">
                <h1>{dish.title}</h1>
                <h2>Why do we love it?</h2>
                <p>{dish.body}</p>
                <h2>Best Places to Try</h2>
                <p className="remark centered">
                  Choose a restaurant to see any foodie meetups
                  <br /> already planned there or start your own
                </p>
                <ul>
                  {dish.locations.filter(loc => loc.city.id === cityId).map((location) => (
                    <Link key={location.id} to={`/restaurants/${location.id}`}>
                      <li className="badge">
                        {location.title}
                      </li>
                    </Link>
                  ))}        
                  <br/>
                  {otherLocations.length > 0 && "also found in these other cities:"}
                  {otherLocations.map((location) => (
                    <Link key={location.id} to={`/restaurants/${location.id}`}>
                      <li className="badge">
                        {location.title} in {location.city.title}
                      </li>
                    </Link>
                  ))}
                </ul>
              </div>
            </div>
            <div className="section">
              <Link
                to="/dishes"
                className="back-button"
                style={{ textDecoration: "underline" }}
              >
                ← Back to the Dishes
              </Link>
            </div>
          </div>
          {!loading && activeEvents?.length > 0 && (
            <div className="section">
              <h2>See events where people are trying {dish.title}</h2>
              <div className="block-wrapper events">
                {activeEvents?.map((event, key) => (
                  <div className="block" key={key}>
                    <h3
                      style={{ cursor: "pointer", fontSize: "1.5rem" }}
                      onClick={() => handleJoinEventClicked(event.id)}
                    >
                      {event.title}
                    </h3>
                    {authed && (
                      <>
                        <h3 className="subheader">Restaurant</h3>
                        <span>{event.location?.title}</span>
                      </>
                    )}
                    <h3 className="subheader">Date & Time</h3>
                    <span>
                      {moment(event.date)
                        .tz(event.timezone)
                        .format("dddd, MMMM Do · h:mm a")}
                    </span>
                    {event.tags.length > 0 && (
                      <>
                        <h3 className="subheader">Meetup Mood</h3>
                        <ul className="tags-list">
                          {event.tags.map((tag) => (
                            <li key={tag.id}>{tag.title}</li>
                          ))}
                        </ul>
                      </>
                    )}
                    {event.clients.length === 4 ? (
                      <p>This event is full</p>
                    ) : (
                      <div className="sm-button-wrapper">
                        <Button
                          color="primary"
                          variant="contained"
                          size="small"
                          className="event-list-button"
                          onClick={() => handleJoinEventClicked(event.id)}
                        >
                          Join this event
                        </Button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </>
  );
}

export default Dish;
